import { useEffect, useState } from 'react';
import {
  CalendarDays,
  MessageSquare,
  Mail,
  Ticket,
  Palette,
  Sparkles,
  Coins,
  LayoutDashboard,
  Phone,
  Bell,
  BookOpen,
  Rss,
  Brain,
  Users,
  Hash,
  type LucideIcon,
} from 'lucide-react';

// ID delle voci che possono comparire nelle shortcut mobile (header in alto
// o barra inferiore). È un catalogo unico condiviso tra i due slot, così
// l'utente può scegliere liberamente quale icona vedere dove.
export type MobileHeaderActionId =
  | 'calendar'
  | 'messaging-chat'
  | 'messaging-email'
  | 'quick-ticket'
  | 'theme-toggle'
  | 'ai-assistant'
  | 'token-usage'
  | 'dashboard'
  | 'sip-phone'
  | 'notifications'
  | 'wiki'
  | 'feed'
  | 'ai-tools'
  | 'messaging-feed'
  | 'messaging-groups'
  | 'messaging-channels';

// Alias semantico per usi futuri/esterni (header e bottom bar condividono lo
// stesso catalogo di voci).
export type MobileShortcutId = MobileHeaderActionId;

export interface MobileHeaderActionMeta {
  id: MobileHeaderActionId;
  label: string;
  description: string;
}

export const MOBILE_HEADER_ACTIONS: MobileHeaderActionMeta[] = [
  { id: 'calendar', label: 'Calendario', description: 'Apri rapidamente il calendario' },
  { id: 'messaging-chat', label: 'Chat aziendale', description: 'Vai alla chat di squadra' },
  { id: 'messaging-email', label: 'Email', description: 'Apri la posta integrata' },
  { id: 'quick-ticket', label: 'Apri ticket', description: 'Crea un ticket di supporto rapido' },
  { id: 'theme-toggle', label: 'Tema chiaro/scuro', description: 'Cambia tema con un tap' },
  { id: 'ai-assistant', label: 'Assistente AI', description: 'Apri il tuo assistente personale (se attivo)' },
  { id: 'token-usage', label: 'Consumo crediti', description: 'Vedi il consumo di token AI' },
  { id: 'dashboard', label: 'Dashboard', description: 'Apri la dashboard principale' },
  { id: 'sip-phone', label: 'Telefono SIP', description: 'Apri il dialer telefonico' },
  { id: 'notifications', label: 'Notifiche', description: 'Apri il pannello delle notifiche' },
  { id: 'wiki', label: 'Wiki aziendale', description: 'Apri la wiki aziendale (se attiva)' },
  { id: 'feed', label: 'Feed', description: 'Vai al feed aziendale (se attivo)' },
  { id: 'ai-tools', label: 'AI Tools', description: 'Apri gli strumenti AI (se attivi)' },
  { id: 'messaging-feed', label: 'Feed aziendale', description: 'Apri il Feed del Messenger' },
  { id: 'messaging-groups', label: 'Gruppi', description: 'Apri i gruppi di lavoro' },
  { id: 'messaging-channels', label: 'Canali', description: 'Apri i canali pubblici' },
];

// Icona Lucide condivisa tra header mobile, barra inferiore e pannelli di
// configurazione. Single source of truth: chiunque renderizzi la shortcut
// (Header, BottomNav, Settings) la prende da qui.
export const MOBILE_ACTION_ICON: Record<MobileHeaderActionId, LucideIcon> = {
  'calendar': CalendarDays,
  'messaging-chat': MessageSquare,
  'messaging-email': Mail,
  'quick-ticket': Ticket,
  'theme-toggle': Palette,
  'ai-assistant': Sparkles,
  'token-usage': Coins,
  'dashboard': LayoutDashboard,
  'sip-phone': Phone,
  'notifications': Bell,
  'wiki': BookOpen,
  'feed': Rss,
  'ai-tools': Brain,
  'messaging-feed': Rss,
  'messaging-groups': Users,
  'messaging-channels': Hash,
};

export const DEFAULT_MOBILE_HEADER_ACTIONS: MobileHeaderActionId[] = [
  'calendar',
  'messaging-chat',
  'messaging-email',
];

export const MAX_MOBILE_HEADER_ACTIONS = 5;

const STORAGE_KEY_PREFIX = 'edgvoip:mobile-header-actions:';
const CHANGED_EVENT = 'edgvoip:mobile-header-actions-changed';

const VALID_IDS = new Set<string>(MOBILE_HEADER_ACTIONS.map((a) => a.id));

function getStorageKey(userId: string | undefined): string | null {
  if (!userId) return null;
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function sanitize(input: unknown): MobileHeaderActionId[] {
  const result: MobileHeaderActionId[] = [];
  const seen = new Set<string>();
  if (Array.isArray(input)) {
    for (const id of input) {
      if (typeof id === 'string' && VALID_IDS.has(id) && !seen.has(id)) {
        result.push(id as MobileHeaderActionId);
        seen.add(id);
        if (result.length >= MAX_MOBILE_HEADER_ACTIONS) break;
      }
    }
  }
  return result;
}

export function getMobileHeaderActions(userId: string | undefined): MobileHeaderActionId[] {
  const key = getStorageKey(userId);
  if (!key || typeof window === 'undefined') return [...DEFAULT_MOBILE_HEADER_ACTIONS];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [...DEFAULT_MOBILE_HEADER_ACTIONS];
    return sanitize(JSON.parse(raw));
  } catch {
    return [...DEFAULT_MOBILE_HEADER_ACTIONS];
  }
}

export function setMobileHeaderActions(
  userId: string | undefined,
  ids: MobileHeaderActionId[],
): MobileHeaderActionId[] {
  const sanitized = sanitize(ids);
  const key = getStorageKey(userId);
  if (key && typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(key, JSON.stringify(sanitized));
      window.dispatchEvent(
        new CustomEvent(CHANGED_EVENT, { detail: { userId, ids: sanitized } }),
      );
    } catch {
      // ignore storage errors (private mode, quota, etc.)
    }
  }
  return sanitized;
}

export function useMobileHeaderActions(
  userId: string | undefined,
): MobileHeaderActionId[] {
  const [ids, setIds] = useState<MobileHeaderActionId[]>(() =>
    getMobileHeaderActions(userId),
  );

  useEffect(() => {
    setIds(getMobileHeaderActions(userId));
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail || !userId || detail.userId === userId) {
        setIds(getMobileHeaderActions(userId));
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === getStorageKey(userId)) {
        setIds(getMobileHeaderActions(userId));
      }
    };
    window.addEventListener(CHANGED_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGED_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [userId]);

  return ids;
}
